import { useParams, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import NotFound from "@/pages/NotFound";
import { Reveal, Eyebrow } from "@/components/Reveal";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { SERVICES, SITE_URL } from "@/data/content";

const ServicePage = () => {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <NotFound />;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      name: service.metaTitle,
      description: service.metaDesc,
      url: `${SITE_URL}/services/${service.slug}`,
      about: { "@type": "MedicalCondition", name: service.name },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: service.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <>
      <SEO
        title={service.metaTitle}
        description={service.metaDesc}
        path={`/services/${service.slug}`}
        jsonLd={jsonLd}
      />
      <PageHero
        eyebrow="Confidential STD/STI Care — Delhi"
        title={service.h1}
        intro={service.intro}
      />

      <section className="py-14 sm:py-20" data-testid="service-content">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 space-y-12">
          {service.sections.map((sec, i) => (
            <Reveal key={sec.heading} delay={i * 0.05}>
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-slate-950">
                {sec.heading}
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">{sec.body}</p>
              {sec.points && (
                <ul className="mt-5 grid sm:grid-cols-2 gap-3">
                  {sec.points.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-emerald-800 mt-0.5 shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
          ))}

          <Reveal>
            <div className="bg-white border border-slate-900/10 rounded-3xl p-7 sm:p-9">
              <Eyebrow>Common Questions</Eyebrow>
              <Accordion type="single" collapsible className="mt-4">
                {service.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`svc-faq-${i}`} data-testid={`service-faq-${i}`}>
                    <AccordionTrigger className="text-left font-medium text-slate-900">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 leading-relaxed">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>

          <Reveal>
            <div className="border-t border-slate-900/10 pt-8">
              <Eyebrow>Related Care</Eyebrow>
              <div className="mt-4 flex flex-wrap gap-2.5">
                {SERVICES.filter((s) => s.slug !== slug).slice(0, 5).map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    data-testid={`related-${s.slug}`}
                    className="inline-flex items-center gap-1.5 bg-white border border-slate-900/10 hover:border-emerald-900/30 text-slate-700 hover:text-emerald-950 text-[13px] font-medium px-4 py-2 rounded-full transition-all"
                  >
                    {s.name} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                ))}
                <Link
                  to="/hiv-pep-delhi"
                  data-testid="related-hiv-pep"
                  className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-300/60 text-amber-900 text-[13px] font-semibold px-4 py-2 rounded-full"
                >
                  HIV PEP (Urgent) <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand
        title={`Consult Dr. Ankur Lal About ${service.name}`}
        text="Confidential consultation — online from your home, or in clinic in Delhi."
      />
    </>
  );
};

export default ServicePage;
